import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import Marquee from "react-fast-marquee";
import { ArrowUpRight, ArrowDown } from "lucide-react";
import { MaskedLines, Reveal, MaskedLinesInView } from "../components/Reveal";
import { IMG } from "../lib/images";
import LatestJobs from "../components/LatestJobs";
import { SERVICES, OFFICES } from "../lib/site";

const EASE = [0.76, 0, 0.24, 1];

function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1.05, 1.2]);

  return (
    <section ref={ref} data-testid="hero" className="relative min-h-screen overflow-hidden flex flex-col justify-end">
      <motion.img
        src={IMG.heroBuilding} alt="Modern corporate office building" style={{ y, scale }}
        className="absolute inset-0 w-full h-full object-cover"
      />
      <div className="absolute inset-0 bg-cobalt/10" />
      <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/45 to-ink/25" />
      <div className="absolute inset-x-0 top-0 h-40 bg-gradient-to-b from-ink/70 to-transparent" />

      <div className="relative z-10 mx-auto max-w-[1500px] w-full px-6 md:px-10 pt-[120px] pb-16 md:pb-24 grid grid-cols-1 lg:grid-cols-12 gap-8 items-end">
        <div className="lg:col-span-8">
          <motion.p
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2 }}
            className="overline text-flame mb-6"
          >
            Staffing HR Direct Services / California · New Jersey
          </motion.p>
          <h1 className="font-display uppercase tracking-tighter leading-[0.85] text-[13vw] lg:text-[6.6vw] text-bone">
            <MaskedLines lines={["Where great", "companies find", <span key="l" className="text-cobalt">great people.</span>]} delay={0.3} />
          </h1>
        </div>
        <div className="lg:col-span-4 lg:pb-2">
          <Reveal delay={1.1}>
            <p className="text-bone/80 text-lg max-w-sm">
              HR staffing, payroll and PEO solutions for Southern California. We deliver top talent and seamless payroll — from contract labor to skilled direct placement across LA, Orange County and the Inland Empire.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-1 gap-3 mt-7">
              <div data-testid="hero-jobseeker-card" className="rounded-2xl bg-cobalt/90 backdrop-blur-md p-5 text-white">
                <p className="overline text-white/70">Looking for work?</p>
                <p className="text-sm text-white/85 mt-1">Browse open jobs or send us your application.</p>
                <div className="flex flex-wrap gap-2 mt-4">
                  <Link to="/apply" data-testid="hero-apply-btn" className="inline-flex items-center gap-2 bg-white text-cobalt px-5 py-2.5 rounded-full overline hover:bg-flame hover:text-white transition-colors duration-300">
                    Apply now <ArrowUpRight size={14} />
                  </Link>
                  <Link to="/jobs" data-testid="hero-jobs-btn" className="inline-flex items-center gap-2 border border-white/50 text-white px-5 py-2.5 rounded-full overline hover:bg-white hover:text-cobalt transition-colors duration-300">
                    View open jobs
                  </Link>
                </div>
              </div>
              <div data-testid="hero-employer-card" className="rounded-2xl bg-ink/60 backdrop-blur-md border border-bone/20 p-5 text-bone">
                <p className="overline text-flame">Hiring? Employers only</p>
                <p className="text-sm text-bone/80 mt-1">For businesses that need workers. Not for job applications.</p>
                <Link to="/employers" data-testid="hero-hire-btn" className="inline-flex items-center gap-2 mt-4 border border-bone/40 text-bone px-5 py-2.5 rounded-full overline hover:bg-bone hover:text-ink transition-colors duration-300">
                  Hire talent <ArrowUpRight size={14} />
                </Link>
              </div>
            </div>
          </Reveal>
        </div>
      </div>

      <div className="relative z-10 mx-auto max-w-[1500px] w-full px-6 md:px-10 pb-6 flex items-center gap-3 text-bone/80 overline">
        <ArrowDown size={16} className="animate-bounce" /> Scroll to explore
      </div>
    </section>
  );
}

function StatBar() {
  const stats = [["10+","Years of expertise"],["6","Offices, CA & NJ"],["9","Industries served"],["100%","Local, dedicated support"]];
  return (
    <section className="bg-ink text-bone relative z-10">
      <div className="mx-auto max-w-[1500px] px-6 md:px-10 py-16 grid grid-cols-2 lg:grid-cols-4 gap-10">
        {stats.map(([n, l], i) => (
          <Reveal key={l} delay={i * 0.08}>
            <div className="border-t border-bone/20 pt-5">
              <div className="font-display text-5xl md:text-6xl tracking-tighter text-flame">{n}</div>
              <div className="text-bone/60 text-sm mt-2">{l}</div>
            </div>
          </Reveal>
        ))}
      </div>
    </section>
  );
}

function EditorialMarquee() {
  const items = ["HR Staffing", "Payroll", "PEO Solutions", "Recruiting & RPO", "Administrative", "Logistics", "Hospitality", "Industrial", "Manufacturing", "Distribution", "Health Care"];
  return (
    <section className="bg-ink text-bone py-7 overflow-hidden select-none" aria-hidden="true">
      <Marquee speed={45} gradient={false}>
        {items.map((t, i) => (
          <span key={i} className="flex items-center font-display uppercase tracking-tight text-3xl md:text-4xl text-bone/90">
            {t}
            <span className="mx-8 text-flame">/</span>
          </span>
        ))}
      </Marquee>
    </section>
  );
}

function Services() {
  return (
    <section data-testid="home-services" className="mx-auto max-w-[1500px] px-6 md:px-10 py-24 md:py-32">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-end mb-16">
        <div className="lg:col-span-7">
          <p className="overline text-cobalt mb-5">What we do</p>
          <h2 className="font-display uppercase tracking-tighter leading-[0.9] text-5xl md:text-7xl">
            <MaskedLinesInView lines={["Full-service", "HR, staffing", "& payroll"]} />
          </h2>
        </div>
        <div className="lg:col-span-4 lg:col-start-9">
          <p className="text-muted-foreground mb-6">From staffing and direct placement to payroll and PEO, we handle the workforce so you can run the business. Explore everything we cover.</p>
          <Link to="/services" data-testid="home-view-services" className="inline-flex items-center gap-2 bg-cobalt text-white px-6 py-3 rounded-full overline hover:bg-flame transition-colors duration-300">
            View all services <ArrowUpRight size={14} />
          </Link>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 border-t border-l border-border">
        {SERVICES.map((s, i) => (
          <Reveal key={s.n} delay={(i % 3) * 0.08}>
            <Link to="/services" data-testid={`home-service-${s.n}`} className="group block border-b border-r border-border p-8 md:p-10 h-full hover:bg-ink hover:text-bone transition-colors duration-300">
              <div className="flex items-start justify-between">
                <span className="font-mono text-sm text-flame">{s.n}</span>
                <ArrowUpRight size={20} className="opacity-40 group-hover:opacity-100 group-hover:text-flame transition-all" />
              </div>
              <h3 className="font-display uppercase tracking-tight text-3xl mt-10 leading-[0.95]">{s.title}</h3>
              <p className="text-sm mt-4 text-muted-foreground group-hover:text-bone/70 transition-colors">{s.desc}</p>
            </Link>
          </Reveal>
        ))}
      </div>
    </section>
  );
}

function Parallax() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["-12%", "12%"]);
  return (
    <section ref={ref} className="relative h-[80vh] overflow-hidden">
      <motion.img src={IMG.warehouse2} alt="Distribution warehouse operations" style={{ y }} className="absolute inset-0 w-full h-[130%] object-cover -top-[15%]" />
      <div className="absolute inset-0 bg-ink/55" />
      <div className="relative z-10 h-full mx-auto max-w-[1500px] px-6 md:px-10 flex items-center">
        <div className="max-w-2xl text-bone">
          <p className="overline text-flame mb-5">The Direct difference</p>
          <h2 className="font-display uppercase tracking-tighter leading-[0.9] text-4xl md:text-6xl">
            <MaskedLinesInView lines={["We don't send", "résumés. We send", "the right people."]} />
          </h2>
          <Reveal delay={0.3}>
            <p className="text-bone/70 text-lg mt-6 max-w-lg">
              Every candidate is screened, verified and matched by your floor, backed by a team that has spent three decades doing exactly this.
            </p>
            <Link to="/about" className="inline-flex items-center gap-2 mt-7 border border-bone/30 px-6 py-3 rounded-full overline hover:bg-bone hover:text-ink transition-colors duration-300">
              Our approach <ArrowUpRight size={14} />
            </Link>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

function LocationsPeek() {
  return (
    <section className="mx-auto max-w-[1500px] px-6 md:px-10 py-24 md:py-32">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 mb-14 items-end">
        <div className="lg:col-span-8">
          <p className="overline text-cobalt mb-5">Where we are</p>
          <h2 className="font-display uppercase tracking-tighter leading-[0.9] text-5xl md:text-7xl">Six offices. One standard.</h2>
        </div>
        <div className="lg:col-span-3 lg:col-start-10">
          <Link to="/locations" className="inline-flex items-center gap-2 overline hover:text-flame transition-colors">
            All locations <ArrowUpRight size={14} />
          </Link>
        </div>
      </div>
      <div className="border-t border-border">
        {OFFICES.map((o, i) => (
          <Reveal key={o.id} delay={i * 0.05}>
            <div className="group flex items-center justify-between border-b border-border py-6 hover:px-4 transition-all duration-300 hover:bg-secondary">
              <div className="flex items-baseline gap-6">
                <span className="font-mono text-xs text-muted-foreground">{String(i + 1).padStart(2, "0")}</span>
                <span className="font-display uppercase tracking-tight text-3xl md:text-5xl group-hover:text-cobalt transition-colors">{o.city}</span>
              </div>
              <span className="overline text-muted-foreground">{o.state} · {o.tag}</span>
            </div>
          </Reveal>
        ))}
      </div>
    </section>
  );
}

function CeoQuote() {
  return (
    <section data-testid="home-ceo" className="bg-secondary/50 border-y border-border">
      <div className="mx-auto max-w-[1500px] px-6 md:px-10 py-20 md:py-28 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center">
        <div className="lg:col-span-7 order-2 lg:order-1">
          <p className="overline text-flame mb-6">A word from our CEO</p>
          <span className="font-display text-cobalt text-7xl leading-none block mb-2">&ldquo;</span>
          <blockquote className="font-display uppercase tracking-tight leading-[1.05] text-2xl md:text-4xl -mt-6">
            No matter your industry, HR Direct will connect your skills with employers who need them. And we will be <span className="text-cobalt">your partner</span> every step of the way!
          </blockquote>
          <div className="mt-8 flex items-center gap-4">
            <div className="w-12 h-[2px] bg-flame" />
            <div>
              <p className="font-display uppercase tracking-tight text-xl">Gabriela Mendoza</p>
              <p className="overline text-muted-foreground">CEO &amp; President · HR Direct Services</p>
            </div>
          </div>
        </div>
        <div className="lg:col-span-5 order-1 lg:order-2 flex justify-center lg:justify-end">
          <Reveal>
            <img src="/gabriela-cutout.png" alt="Gabriela Mendoza, CEO & President" className="w-full max-w-[320px] h-auto object-contain drop-shadow-2xl" data-testid="ceo-photo" />
          </Reveal>
        </div>
      </div>
    </section>
  );
}

export default function Home() {
  return (
    <div data-testid="home-page">
      <Hero />
      <StatBar />
      <EditorialMarquee />
      <LatestJobs />
      <Services />
      <Parallax />
      <CeoQuote />
      <LocationsPeek />
    </div>
  );
}
